import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Row, Col, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { BASE_URL } from '../../Config';
import 'bootstrap/dist/css/bootstrap.min.css';

const RegisterAgent = ({
  companyUserId: companyUserIdProp,
  companyName: companyNameProp,
  embedded = false,
}) => {
  const [userId, setUserId] = useState(companyUserIdProp || '');
  const [companyName, setCompanyName] = useState(companyNameProp || '');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [address, setAddress] = useState('');
  const [cnic, setCnic] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (companyUserIdProp) {
      setUserId(companyUserIdProp);
    }
    if (companyNameProp) {
      setCompanyName(companyNameProp);
    }

    if (companyUserIdProp && companyNameProp) return;

    const storedUserData = sessionStorage.getItem('user');
    if (storedUserData) {
      const userData = JSON.parse(storedUserData);
      if (!companyUserIdProp) {
        if (userData.userRole === 'Manager') {
          setUserId(userData.selectedCompanyId || userData.companyId || '');
        } else {
          setUserId(userData.userId || userData.companyId || '');
        }
      }
      if (!companyNameProp) {
        setCompanyName(
          userData.selectedCompanyName ||
            userData.companyName ||
            (userData.userRole === 'Company' ? userData.name : '') ||
            ''
        );
      }
    } else if (!companyUserIdProp) {
      Swal.fire('Error', 'Your session has expired. Please sign in again.', 'error');
    }
  }, [companyUserIdProp, companyNameProp]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userId) {
      Swal.fire('Error', 'Company not selected. Please select a company first.', 'error');
      return;
    }

    if (!name || !email || !password) {
      Swal.fire('Error', 'Please complete all required fields.', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${BASE_URL}Register_Agent`,
        {
          userId,
          name,
          email,
          password,
          address,
          cnic,
          contactNumber,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            accept: '*/*',
          },
        }
      );

      Swal.fire({
        title: response.data.responseCode === '0000' ? 'Success' : 'Error',
        text: response.data.message,
        icon: response.data.responseCode === '0000' ? 'success' : 'error',
      });

      if (response.data.responseCode === '0000') {
        setName('');
        setEmail('');
        setPassword('');
        setAddress('');
        setCnic('');
        setContactNumber('');
      }
    } catch (error) {
      console.error('Register Agent Error:', error);
      Swal.fire('Error', error.response?.data?.message || 'An unexpected error occurred. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={embedded ? '' : 'co-page'}>
      <div className={embedded ? '' : 'container-xl mt-3'}>
        <div className={embedded ? '' : 'card co-card p-3 p-md-4'}>
        {!embedded && <h2 className="text-center mb-4 h4">Register New Agent</h2>}
        {embedded && <h5 className="mb-3">Agent Details</h5>}

        {companyName && (
          <Alert variant="light" className="border small py-2">
            Company: <strong>{companyName}</strong>
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <Row className="g-3">
            <Col xs={12} md={6}>
              <Form.Floating>
                <Form.Control
                  id="agentName"
                  placeholder="Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
                <label htmlFor="agentName">Name *</label>
              </Form.Floating>
            </Col>
            <Col xs={12} md={6}>
              <Form.Floating>
                <Form.Control
                  id="agentEmail"
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <label htmlFor="agentEmail">Email *</label>
              </Form.Floating>
            </Col>
            <Col xs={12} md={6}>
              <div className="input-group">
                <div className="form-floating flex-grow-1">
                  <input
                    id="agentPassword"
                    type={showPassword ? 'text' : 'password'}
                    className="form-control"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <label htmlFor="agentPassword">Password *</label>
                </div>
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                >
                  <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`} />
                </button>
              </div>
            </Col>
            <Col xs={12} md={6}>
              <Form.Floating>
                <Form.Control
                  id="agentContact"
                  placeholder="Contact Number"
                  value={contactNumber}
                  onChange={(e) => setContactNumber(e.target.value)}
                />
                <label htmlFor="agentContact">Contact Number</label>
              </Form.Floating>
            </Col>
            <Col xs={12} md={6}>
              <Form.Floating>
                <Form.Control
                  id="agentCnic"
                  placeholder="CNIC"
                  value={cnic}
                  onChange={(e) => setCnic(e.target.value)}
                />
                <label htmlFor="agentCnic">CNIC</label>
              </Form.Floating>
            </Col>
            <Col xs={12} md={6}>
              <Form.Floating>
                <Form.Control
                  id="agentAddress"
                  placeholder="Address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
                <label htmlFor="agentAddress">Address</label>
              </Form.Floating>
            </Col>
          </Row>

          <div className="d-grid d-md-flex justify-content-md-end mt-4">
            <Button type="submit" className="px-4" disabled={loading}>
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Registering...
                </>
              ) : (
                'Register Agent'
              )}
            </Button>
          </div>
        </Form>
        </div>
      </div>
    </div>
  );
};

export default RegisterAgent;
